/**
 * The Commercial Hub's tool registry.
 *
 * One list, read by the hub tiles, the admin access matrix, and (later) the API's
 * access checks — so adding a portal is adding an entry here, not a new project.
 * This is the whole point of the hub: stop the sprawl of standalone portals, each
 * with its own login, deploy, and look.
 *
 * `id` values are persisted in `hub_access.module_id`. Renaming one orphans every
 * grant that references it, so treat them as permanent.
 */
/**
 * Is the Commercial Hub open to members yet?
 *
 * FALSE until Josh says otherwise (2026-08-28). While it is false, every /hub
 * route and every hub API is staff-only: a member who types the URL is sent to
 * COMM-PASS, and the APIs answer as though the feature does not exist.
 *
 * This is the ONE switch. Flip it to true and the hub opens — routes, Connor,
 * the questionnaires, all of it — with no other edit. It lives here rather than
 * in an env var so that opening the hub is a reviewable commit rather than a
 * dashboard toggle somebody makes at 6pm.
 *
 * Members keep using COMM-PASS exactly as they do today either way. The hub has
 * always been additive; this makes that a guarantee rather than a convention.
 */
export const HUB_OPEN_TO_MEMBERS = false;
/** Effective access for one person, given the explicit decisions on record. */
export function hasModuleAccess(m, explicit) {
    const decision = explicit[m.id];
    return decision === undefined ? m.defaultOn : decision;
}
export const HUB_ROLE_LABEL = {
    agency_admin: 'Admin',
    agency_member: 'Member',
};
/**
 * Whether a person can actually open a module, all three gates applied.
 *
 * The gates answer different questions and all must pass: is the agency allowed
 * here at all, and has this person been given this tool. Keeping them separate
 * means a nightly Salesforce sync can switch an agency off without touching
 * anyone's individual grants — so switching it back on restores what they had.
 */
export function canOpenModule(opts) {
    if (!opts.agencyEnabled)
        return false;
    return hasModuleAccess(opts.module, opts.decisions);
}
/**
 * ⚠️ ARRAY ORDER IS RAIL ORDER. The left rail is generated from this list now,
 * so moving an entry moves it on screen. Kept in the order somebody works in:
 * find a market, then assemble, then the things you do to a policy that already
 * exists.
 */
export const HUB_MODULES = [
    {
        id: 'commpass',
        name: 'COMM-PASS',
        tagline: 'Send us a commercial risk and our specialists market it for you.',
        href: '/new',
        external: false,
        status: 'live',
        surface: 'tile',
        audience: 'both',
        defaultOn: true,
        // The front door, reached from the hub itself rather than sitting in the
        // rail alongside the tools.
        rail: false,
        icon: 'M4 4h16v6H4zM4 14h16v6H4z',
        guide: {
            does: 'You send us the risk. An Agents Alliance specialist markets it, negotiates, and comes back to you with quotes.',
            useWhen: 'The account is complicated, you are short of time, or you want our leverage with a market rather than your own.',
            notFor: 'A quick placement you already know where to send. You will wait on us for something you could have done yourself.',
            who: 'Agents Alliance does the work.',
        },
    },
    {
        id: 'appetite',
        name: 'Appetite',
        tagline: 'Ask which of our carriers want a risk, and get pointed at what comes next.',
        // Appetite IS Connor — there is no separate page, and inventing one would
        // mean two front doors to the same answer. The rail opens the chat.
        href: '/hub',
        external: false,
        status: 'beta',
        surface: 'assistant',
        audience: 'both',
        // Asking "can we place this?" is the question that precedes everything else
        // on the hub. Gating it would just mean people keep asking a colleague.
        defaultOn: true,
        // ⚠️ OFF THE RAIL (Josh, 2026-09-05): "all it does is open Connor". The chat
        // is docked on every page already, so the entry was a second door to a room
        // you are always standing in.
        rail: false,
        icon: 'M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4',
        guide: {
            does: 'Ask Connor which of our appointed markets fit a risk, with the guide each answer came from.',
            useWhen: 'Before you build anything — knowing there is no market saves the hour you were about to spend.',
            notFor: 'A binding decision. It is guidance; a carrier can still decline.',
            who: 'Instant, no waiting on anyone.',
        },
    },
    {
        id: 'submission',
        name: 'Submission Portal',
        tagline: 'Build your own submission, and see how it grades before it goes out.',
        href: '/submission',
        external: false,
        status: 'planned',
        surface: 'tile',
        audience: 'member',
        defaultOn: false,
        rail: true,
        icon: 'M4 4h11l5 5v11H4zM8 12h8M8 16h5',
        guide: {
            does: 'You build the submission yourself — documents in, account filled from them, graded, then emailed to your own underwriters.',
            useWhen: 'You know your markets and want the paperwork assembled fast, in your name, with the replies coming to you.',
            notFor: 'An account you want marketed for you. Nothing here reaches an Agents Alliance specialist.',
            who: 'You do the work. AAS has no part in it.',
        },
    },
    {
        id: 'supplementals',
        name: 'Supplemental Applications',
        tagline: 'Find the supplemental application for a class, without hunting for it.',
        href: '/hub/supplementals',
        external: false,
        status: 'beta',
        surface: 'resource',
        audience: 'both',
        // The forms are carriers' own public PDFs, and finding one is the problem
        // everybody already has — no reason to make anyone ask for access.
        defaultOn: true,
        rail: true,
        icon: 'M6 3h9l4 4v14H6zM14 3v5h5',
        guide: {
            does: 'The carrier supplemental for a class, as a form you can fill in or send to the insured to complete.',
            useWhen: 'A market has asked for one, or you want the insured to answer the parts only they know.',
            notFor: 'Classes we do not hold a form for yet — the carrier’s own site will have it.',
            who: 'You, or your insured, or both.',
        },
    },
    {
        /**
         * The one item on the roadmap that depended on nothing else — no canonical
         * submission model, no ACORD licence question, no AMS wizard. It became
         * buildable when the carrier contact book landed, because the reason loss
         * run requests are manual today is that nobody holds carrier addresses.
         *
         * Off by default while it is proven. Nothing about it is Iron Peak-shaped,
         * so it is an ordinary grant rather than a separate access level.
         */
        id: 'lossruns',
        name: 'Loss Runs',
        tagline: 'Ask a prior carrier for loss runs, and keep chasing until they answer.',
        href: '/hub/loss-runs',
        external: false,
        status: 'beta',
        surface: 'tile',
        audience: 'both',
        defaultOn: false,
        // ⚠️ INSIDE THE REQUESTS CENTER, NOT ON THE RAIL (Josh, 2026-09-05): "Loss
        // Runs, Report a Loss and Binder Requests would all be within the Requests
        // Center". Still its own page, its own table and its own chase — only the
        // way in changed.
        within: 'requests',
        rail: false,
        icon: 'M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z',
        guide: {
            does: 'Ask a prior carrier for loss runs, and keep asking. It follows up on its own until the reports arrive.',
            useWhen: 'Any renewal or new business where the loss history is missing and a quote is waiting on it.',
            notFor: 'Making a carrier answer faster than they were going to. It chases and it keeps the record; it cannot conjure a report.',
            who: 'You send it once. The chasing runs itself.',
        },
    },
    {
        /**
         * ⚠️ THE NOTICE IS AGENCY WORK; THE CLAIM IS THE CARRIER'S. That line is
         * Josh's and it is why this exists while Claims Resources was cut — we are
         * not the carrier, but the insured rings their agent, and the agent has to
         * tell somebody. The exposure is late reporting, so a record of what went
         * where and when reduces E&O rather than adding to it.
         */
        id: 'fnol',
        name: 'Report a Loss',
        tagline: 'Tell the carrier about a loss, and keep the proof that you did.',
        href: '/hub/report-a-loss',
        external: false,
        status: 'beta',
        surface: 'tile',
        audience: 'both',
        defaultOn: false,
        // ⚠️ INSIDE THE REQUESTS CENTER, NOT ON THE RAIL (Josh, 2026-09-05): "Loss
        // Runs, Report a Loss and Binder Requests would all be within the Requests
        // Center". Still its own page, its own table and its own chase — only the
        // way in changed.
        within: 'requests',
        rail: false,
        icon: 'M12 9v4M12 17h.01M10.3 4.3L2.5 18a1.5 1.5 0 001.3 2.2h16.4a1.5 1.5 0 001.3-2.2L13.7 4.3a1.5 1.5 0 00-2.6 0z',
        guide: {
            does: 'Report a loss to the carrier’s claims desk, and keep the proof — what went, when it went, and the claim number that came back.',
            useWhen: 'As soon as an insured tells you about a loss. Late reporting is what prejudices a claim, so this is worth doing before you have all the details.',
            notFor: 'Anything after the claim is open. The adjuster runs it from there — we are not the carrier and this does not follow a claim through.',
            who: 'You report it; the carrier handles the claim.',
        },
    },
    {
        /**
         * ⚠️ THE DEADLINE IS THE EFFECTIVE DATE, WHICH IS WHAT MAKES THIS NOT LOSS
         * RUNS. A loss run request that has waited eleven days is the urgent one; a
         * binder request sent this morning for coverage incepting tomorrow beats one
         * sent three weeks ago for a January renewal. Everything on the page sorts
         * and colours by days to inception, not days since sending.
         */
        id: 'binders',
        name: 'Binder Requests',
        tagline: 'Formally ask an underwriter to bind, and keep the binder they send back.',
        href: '/hub/binders',
        external: false,
        status: 'beta',
        surface: 'tile',
        audience: 'both',
        defaultOn: false,
        // ⚠️ INSIDE THE REQUESTS CENTER, NOT ON THE RAIL (Josh, 2026-09-05): "Loss
        // Runs, Report a Loss and Binder Requests would all be within the Requests
        // Center". Still its own page, its own table and its own chase — only the
        // way in changed.
        within: 'requests',
        rail: false,
        icon: 'M9 12l2 2 4-4M7 3h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z',
        guide: {
            does: 'Sends a formal bind request to the underwriter holding the quote, chases it as inception approaches, and records the binder number that comes back.',
            useWhen: 'The quote is accepted and coverage has to be in force by a date — a closing, a lender, an expiring policy.',
            notFor: 'Getting a quote in the first place. This asks a market that has already quoted to make it real.',
            who: 'You ask; the underwriter binds.',
        },
    },
    {
        /**
         * ⚠️ THE ONLY PROGRAMME WHOSE LETTER THE MEMBER DOES NOT SIGN. Loss Runs,
         * Report a Loss and Binder Requests are the agency writing to a carrier. A
         * broker-of-record letter is the INSURED instructing their carrier to change
         * who represents them, and it has force only because the policyholder signed
         * it. So the wait it models is on somebody outside the agency, which is why
         * its Requests Center row reads "Awaiting signature" before it ever reads
         * "With the carrier".
         */
        id: 'bor',
        name: 'BOR / AOR Requests',
        tagline: 'Move an account onto your paper — the insured signs once, every carrier is told.',
        href: '/hub/bor',
        external: false,
        status: 'beta',
        surface: 'tile',
        audience: 'both',
        defaultOn: false,
        within: 'requests',
        rail: false,
        // A signed document changing hands.
        icon: 'M9 12h6m-6 4h6M8 3h8a2 2 0 012 2v14a2 2 0 01-2 2H8a2 2 0 01-2-2V5a2 2 0 012-2zM9 7h6',
        guide: {
            does: 'Builds the broker-of-record letter, emails it to the insured to sign once, and sends the signed letter to every carrier named on it.',
            useWhen: 'You are taking over an account and the carriers have to be told who represents it now.',
            notFor: 'Changing or cancelling coverage. A BOR moves representation and nothing else — the policy is untouched until somebody asks for a change separately.',
            who: 'You prepare it; the insured signs it; the carrier acts on it.',
        },
    },
    {
        /**
         * ⚠️ A VIEW OVER THREE PROGRAMMES, NOT A FOURTH PROGRAMME. It creates
         * nothing and owns no table — it reads Loss Runs, Report a Loss and Binder
         * Requests and answers one question: what am I waiting on. Granting it to
         * somebody who holds none of the three shows an empty list, which is
         * correct rather than broken.
         */
        id: 'requests',
        name: 'Requests Center',
        tagline: 'Everything you have asked a carrier for and not yet got back.',
        href: '/hub/requests',
        external: false,
        status: 'beta',
        surface: 'tile',
        audience: 'both',
        defaultOn: false,
        rail: true,
        icon: 'M4 5h16v14H4zM8 9h8M8 13h5',
        guide: {
            does: 'One list of every outstanding loss run, reported loss and binder request, ordered by what is most overdue.',
            useWhen: 'First thing in the morning, to see what a carrier still owes you.',
            notFor: 'Starting a request — each kind has its own page for that. This is the waiting-on list.',
            who: 'You asked; a carrier owes you an answer.',
        },
    },
    {
        /**
         * ⚠️ IRON PEAK ONLY, AND THAT IS THE POINT (Josh, 2026-08-31). Iron Peak
         * members write business Agents Alliance has to enter into AMS360 by hand:
         * today they keep a spreadsheet on the desktop, fill it in, save it, and
         * email it to Cyndy. Everyone else in the alliance would open this and have
         * no idea what it was, so it is off by default and granted deliberately.
         *
         * Which agencies get it is decided by Agency_Access_Level__c in Salesforce
         * — "Iron Peak", or an access level meaning no AMS/AAS entry — synced by
         * scripts/sync-ironpeak.mjs rather than maintained by hand.
         */
        id: 'ironpeak',
        name: 'Policy Upload',
        tagline: 'Send us a policy you have written, and we enter it into AMS360.',
        href: '/hub/policy-upload',
        external: false,
        status: 'live',
        surface: 'tile',
        audience: 'member',
        defaultOn: false,
        rail: true,
        icon: 'M12 16V4M8 8l4-4 4 4M4 20h16',
        guide: {
            does: 'Send us a policy you have written, and we enter it into AMS360 for you.',
            useWhen: 'Any policy your agency writes that Agents Alliance has to record. This replaces the spreadsheet.',
            notFor: 'Getting a risk quoted — that is COMM-PASS or the Submission Portal.',
            who: 'Agents Alliance keys it in.',
        },
    },
    {
        /**
         * ⚠️ IT IS FOR BOTH ENDS OF THE ROOM, NOT THE NEW PEOPLE (Josh, 2026-09-05:
         * "This portal is to be used by brand new to commercial agents and seasoned
         * vets"). That is why entries carry a plain definition *and* a separate
         * note on the part that actually bites, and why "Start here" filters rather
         * than hides — a glossary that decides some terms are too advanced for you
         * and withholds them is worse than no glossary.
         *
         * On by default. It is reference material with nothing confidential in it,
         * and a glossary somebody has to request access to is a glossary nobody
         * reads.
         */
        id: 'glossary',
        name: 'Glossary',
        tagline: 'Look up any commercial insurance term, in plain English.',
        href: '/hub/glossary',
        external: false,
        status: 'live',
        surface: 'resource',
        audience: 'both',
        defaultOn: true,
        rail: true,
        icon: 'M4 5a2 2 0 012-2h8l4 4v12a2 2 0 01-2 2H6a2 2 0 01-2-2zM8 11h7M8 15h5',
        guide: {
            does: 'Defines the terms, abbreviations and phrases used across commercial insurance — what each one means, the part that trips people up, and the term it gets confused with.',
            useWhen: 'Any time a carrier email, a quote or a contract uses a word you would rather not ask about. "Start here" is the short list if commercial lines is new to you.',
            notFor: 'Deciding whether something is actually covered. It explains what words mean; only the policy in front of you says what it does.',
            who: 'Instant, no waiting on anyone.',
        },
    },
    {
        /**
         * ⚠️ THE SAME ENGINE AS THE INSPECTOR'S PANEL, NOT A SECOND ONE (Josh,
         * 2026-09-07: "Let's make it its own tool. People may want to use it stand
         * alone. But we could also incorporate into the Account Inspector tool as
         * it probably is now"). Both read the same account, the same two sides and
         * the same server-side diff. What differs is the way in: the Inspector
         * assumes you came to analyse an account and found yourself comparing;
         * this assumes comparing is the whole job, so it asks for two policies and
         * nothing else.
         *
         * A second implementation would have been the obvious mistake here — two
         * answers to "did the umbrella survive the renewal", disagreeing the first
         * time one of them was fixed.
         */
        id: 'compare',
        name: 'Policy Comparison',
        tagline: 'Put the expiring policy against the proposed one and see what changed.',
        href: '/hub/compare',
        external: false,
        status: 'beta',
        surface: 'tile',
        audience: 'both',
        defaultOn: false,
        rail: true,
        icon: 'M4 5h6v14H4zM14 5h6v14h-6zM10 12h4',
        guide: {
            does: 'Reads two sets of policy documents side by side and lists what changed — lines that are on one and not the other, carrier changes, premium moves, and the schedules that grew or shrank.',
            useWhen: 'A renewal has come back and you want to know what quietly moved, or you are putting a competitor’s programme against your own.',
            notFor: 'Judging whether coverage got better or worse. Limits are read as written and shown side by side; nothing here says one is broader than the other, and deductibles, sublimits and endorsements are not extracted at all.',
            who: 'Automated, from documents you already have.',
        },
    },
    {
        id: 'inspector',
        name: 'Account Inspector',
        tagline: 'Upload an account’s policies and get an exposure workbook and loss summary back.',
        /*
          ⚠️ A ROUTE NOW, NOT A LINK OUT (Josh, 2026-09-04: "the analyzer link takes
          you to a portal outside of the system and I don't want that"). It used to
          be the absolute URL below, so opening it left the hub for a different
          application with a different login and no way back.
    
          The separate deployment at that URL is NOT retired — one agent still uses
          it, and it reads the same database this route reads. Nothing was copied or
          migrated; see apps/api/src/inspector.ts.
    
          Was: https://commercial-submission-tool-production.up.railway.app
        */
        href: '/hub/inspector',
        external: false,
        status: 'live',
        surface: 'tile',
        audience: 'both',
        defaultOn: false,
        rail: true,
        icon: 'M4 6h16M4 12h16M4 18h10',
        guide: {
            /*
              ⚠️ NOT ONLY RENEWALS (Josh, 2026-09-04). This said "upload a renewal" and
              listed new business under `notFor`, which had it exactly backwards: one
              of the two main uses is taking a prospect's policies from their current
              agency and running them through to find what the incumbent missed.
            */
            does: 'Reads an account’s policies and loss runs and returns an exposure workbook plus a client-ready coverage review.',
            useWhen: 'A renewal you have not looked at properly, an account you have just taken over, or a prospect whose current policies you want to pick apart.',
            notFor: 'An account you hold no paperwork for — it reads documents, it does not go and find them.',
            who: 'Automated, from documents you already have.',
        },
    },
];
export const moduleById = (id) => HUB_MODULES.find((m) => m.id === id);
/** Modules a given audience can ever see, before per-person grants are applied. */
export function modulesFor(audience) {
    return HUB_MODULES.filter((m) => m.audience === audience || m.audience === 'both');
}
